/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { RestaurantCustomer } from "@lemmonhq/types";

export interface CustomerSearchPanelProps {
  customerSearch: string;
  onCustomerSearch: (value: string) => void;
  isSearching: boolean;
  searchResults: RestaurantCustomer[];
  searchError: string;
  onSelectCustomer: (customer: RestaurantCustomer) => void;
}

export const CustomerSearchPanel = defineComponent<CustomerSearchPanelProps>({
  name: "CustomerSearchPanel",
  props: [
    "customerSearch",
    "onCustomerSearch",
    "isSearching",
    "searchResults",
    "searchError",
    "onSelectCustomer",
  ],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div class="space-y-2.5">
        {/* Search input */}
        <div class="relative">
          <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40">
            <span class="material-symbols-outlined text-sm">search</span>
          </span>
          <input
            type="text"
            class="input input-sm input-bordered w-full pl-9 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
            placeholder={t("reservations.customer_search.placeholder")}
            value={props.customerSearch}
            onInput={(e) =>
              props.onCustomerSearch((e.target as HTMLInputElement).value)
            }
          />
          {props.customerSearch.length > 0 && (
            <button
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content/60"
              onClick={() => props.onCustomerSearch("")}
            >
              <span class="material-symbols-outlined text-sm">close</span>
            </button>
          )}
        </div>

        {props.searchError && (
          <div class="text-xs text-error">{props.searchError}</div>
        )}

        {/* Results */}
        <div class="max-h-48 overflow-y-auto space-y-1">
          {props.isSearching ? (
            <div class="flex items-center justify-center py-4">
              <span class="loading loading-spinner loading-sm text-primary" />
            </div>
          ) : props.searchResults.length > 0 ? (
            props.searchResults.map((customer) => {
              const displayName =
                customer.name || customer.email || customer.phone || "?";
              const contact = [customer.email, customer.phone]
                .filter(Boolean)
                .join(" \u2022 ");
              return (
                <button
                  key={customer.id}
                  class="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border border-base-300/30 hover:border-primary/40 hover:bg-base-200/50 transition-all text-left"
                  onClick={() => props.onSelectCustomer(customer)}
                >
                  <div class="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span class="text-primary font-semibold text-xs">
                      {displayName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-accent truncate">
                      {displayName}
                    </p>
                    {contact && (
                      <p class="text-[11px] text-base-content/40 truncate">
                        {contact}
                      </p>
                    )}
                  </div>
                </button>
              );
            })
          ) : props.customerSearch.trim().length >= 2 ? (
            <div class="text-center py-4">
              <span class="material-symbols-outlined text-xl text-base-content/25">
                person_search
              </span>
              <p class="text-xs text-base-content/40 mt-1">
                {t("reservations.customer_search.no_results")}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
});
